import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";
import useDebouncedValue from "../hooks/useDebouncedValue";

const MODULE_TYPE_TO_SHORT_NAME = {
  "IT Quad Module": "qmod",
  "IT Double Module": "dmod",
  "IT 3D Module": "3dmod",
};
const MODULE_TYPE_OPTIONS = Object.keys(MODULE_TYPE_TO_SHORT_NAME);
const SHORT_NAME_TO_MODULE_TYPE = Object.fromEntries(
  Object.entries(MODULE_TYPE_TO_SHORT_NAME).map(([moduleType, shortName]) => [
    shortName,
    moduleType,
  ])
);
const SEARCH_FIELDS = {
  NAME_LABEL: {
    label: "Name label",
    placeholder: "Search module by name label...",
  },
  SERIAL_NUMBER: {
    label: "Serial number",
    placeholder: "Search module by serial number...",
  },
};
const DEFAULT_SEARCH_FIELD = "NAME_LABEL";

const EMPTY_VALUES = {
  part: {},
  attributes: {},
  extension: {},
  readOnly: {},
};

function cloneValues(values) {
  return JSON.parse(JSON.stringify(values || EMPTY_VALUES));
}

function normalizeFormValue(value) {
  if (value === undefined || value === "") return null;
  return value;
}

function fieldValue(values, section, key) {
  const value = values?.[section]?.[key];
  return value === null || value === undefined ? "" : String(value);
}

function fieldChanged(originalValues, nextValues, section, key) {
  return (
    String(normalizeFormValue(fieldValue(originalValues, section, key)) || "") !==
    String(normalizeFormValue(fieldValue(nextValues, section, key)) || "")
  );
}

function buildChangedPayload(schema, originalValues, nextValues) {
  const payload = { part: {}, attributes: {}, extension: {} };

  (schema?.partFields || []).forEach((field) => {
    if (fieldChanged(originalValues, nextValues, "part", field.key)) {
      payload.part[field.key] = normalizeFormValue(fieldValue(nextValues, "part", field.key));
    }
  });

  (schema?.attributeFields || []).forEach((field) => {
    if (fieldChanged(originalValues, nextValues, "attributes", field.key)) {
      payload.attributes[field.key] = normalizeFormValue(
        fieldValue(nextValues, "attributes", field.key)
      );
    }
  });

  (schema?.extensionFields || []).forEach((field) => {
    if (fieldChanged(originalValues, nextValues, "extension", field.key)) {
      payload.extension[field.key] = normalizeFormValue(
        fieldValue(nextValues, "extension", field.key)
      );
    }
  });

  return payload;
}

function payloadHasChanges(payload) {
  return ["part", "attributes", "extension"].some(
    (section) => Object.keys(payload?.[section] || {}).length > 0
  );
}

function mergeChangedValues(values, changes) {
  const merged = cloneValues(values);

  ["part", "attributes", "extension"].forEach((section) => {
    merged[section] = merged[section] || {};
    Object.entries(changes?.[section] || {}).forEach(([key, value]) => {
      merged[section][key] = value === undefined ? null : value;
    });
  });

  return merged;
}

function formatErrorPayload(data, fallback) {
  if (!data) return fallback;
  if (typeof data.detail === "string") return data.detail;
  if (data.detail?.message) return data.detail.message;
  return fallback;
}

function moduleTypeFromQuery(value) {
  if (!value) return "";
  const normalized = value.trim();
  if (MODULE_TYPE_TO_SHORT_NAME[normalized]) return normalized;
  return SHORT_NAME_TO_MODULE_TYPE[normalized.toLowerCase()] || "";
}

function searchFieldFromQuery(value) {
  const normalized = (value || DEFAULT_SEARCH_FIELD).trim().toUpperCase();
  if (normalized === "NAME_LABEL" || normalized === "NAMELABEL" || normalized === "NAME") {
    return "NAME_LABEL";
  }
  if (normalized === "SERIAL_NUMBER" || normalized === "SERIALNUMBER" || normalized === "SERIAL") {
    return "SERIAL_NUMBER";
  }
  return DEFAULT_SEARCH_FIELD;
}

function parseEditModuleQuery(search) {
  const params = new URLSearchParams(search);
  return {
    moduleType: moduleTypeFromQuery(params.get("type")),
    searchField: searchFieldFromQuery(params.get("search_by") || params.get("searchBy")),
    moduleSearchText: params.get("search") || "",
    selectedModule: params.get("module") || "",
    useDev: params.get("dev") === "true",
  };
}

function normalizedSearch(search) {
  return search.startsWith("?") ? search.slice(1) : search;
}

function buildEditModuleSearch(search, state) {
  const params = new URLSearchParams(search);
  const shortName = MODULE_TYPE_TO_SHORT_NAME[state.moduleType];

  if (shortName) {
    params.set("type", shortName);
  } else {
    params.delete("type");
  }

  if (state.selectedModule) {
    params.set("module", state.selectedModule);
  } else {
    params.delete("module");
  }

  if (
    state.moduleType ||
    state.selectedModule ||
    state.moduleSearchText ||
    state.searchField !== DEFAULT_SEARCH_FIELD
  ) {
    params.set("search_by", state.searchField || DEFAULT_SEARCH_FIELD);
  } else {
    params.delete("search_by");
  }
  params.delete("searchBy");

  if (state.moduleSearchText) {
    params.set("search", state.moduleSearchText);
  } else {
    params.delete("search");
  }

  if (state.useDev) {
    params.set("dev", "true");
  } else {
    params.delete("dev");
  }

  return params.toString();
}

export default function EditModule() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const apiUrl = window.APP_CONFIG?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const initialQueryState = parseEditModuleQuery(location.search);
  const applyingUrlState = useRef(false);
  const lastWrittenSearch = useRef(null);

  const [moduleType, setModuleType] = useState(initialQueryState.moduleType);
  const [searchField, setSearchField] = useState(initialQueryState.searchField);
  const [moduleSearchText, setModuleSearchText] = useState(initialQueryState.moduleSearchText);
  const debouncedModuleSearchText = useDebouncedValue(moduleSearchText);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [selectedModule, setSelectedModule] = useState(initialQueryState.selectedModule);
  const [moduleDetails, setModuleDetails] = useState(null);
  const [originalValues, setOriginalValues] = useState(null);
  const [editValues, setEditValues] = useState(null);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingModuleDetails, setLoadingModuleDetails] = useState(false);
  const [updatingModule, setUpdatingModule] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [updateStatus, setUpdateStatus] = useState("");
  const [updateResult, setUpdateResult] = useState(null);
  const [useDev, setUseDev] = useState(initialQueryState.useDev);

  const changedPayload = useMemo(
    () => buildChangedPayload(moduleDetails?.schema, originalValues, editValues),
    [moduleDetails, originalValues, editValues]
  );
  const hasPendingChanges = payloadHasChanges(changedPayload);

  const displayedModuleOptions = useMemo(() => {
    if (selectedModule && !moduleOptions.includes(selectedModule)) {
      return [selectedModule, ...moduleOptions];
    }
    return moduleOptions;
  }, [moduleOptions, selectedModule]);

  const clearLoadedDetails = useCallback(() => {
    setModuleDetails(null);
    setOriginalValues(null);
    setEditValues(null);
    setUpdateStatus("");
    setUpdateResult(null);
    setFieldErrors({});
  }, []);

  const resetDetails = useCallback(() => {
    setSelectedModule("");
    clearLoadedDetails();
  }, [clearLoadedDetails]);

  const fetchModuleOptions = useCallback(
    async (searchText, signal) => {
      if (!auth.isAuthenticated || !moduleType) return;

      const shortName = MODULE_TYPE_TO_SHORT_NAME[moduleType];
      if (!shortName) {
        setError(`Unsupported module type: ${moduleType}`);
        setModuleOptions([]);
        return;
      }

      setLoadingModules(true);
      setError("");
      setFieldErrors({});
      setModuleOptions([]);

      try {
        const url = `${apiUrl}/api/v1/parts/${shortName}?nlf=${encodeURIComponent(
          searchText
        )}&search_by=${searchField}&dev=${useDev ? "true" : "false"}`;
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${auth.user?.access_token}`,
          },
          credentials: "include",
          signal,
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch modules: ${res.status}`);
        }

        const data = await res.json();
        if (signal.aborted) return;
        setModuleOptions(data);
      } catch (err) {
        if (signal.aborted || err.name === "AbortError") return;
        setError(err.message || "Failed to load module options");
        setModuleOptions([]);
      } finally {
        if (!signal.aborted) setLoadingModules(false);
      }
    },
    [auth, moduleType, searchField, useDev, apiUrl]
  );

  const fetchModuleDetails = useCallback(async () => {
    if (!auth.isAuthenticated || !selectedModule) return;

    const shortName = MODULE_TYPE_TO_SHORT_NAME[moduleType];
    if (!shortName) {
      setError(`Unsupported module type: ${moduleType}`);
      setModuleDetails(null);
      return;
    }

    setLoadingModuleDetails(true);
    setError("");

    try {
      const url = `${apiUrl}/api/v1/modules/${shortName}/${encodeURIComponent(
        selectedModule
      )}/edit?identifier=${searchField}&dev=${useDev ? "true" : "false"}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${auth.user?.access_token}`,
        },
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch module details: ${res.status}`);
      }

      const data = await res.json();
      const values = cloneValues(data.values);
      setModuleDetails(data);
      setOriginalValues(values);
      setEditValues(cloneValues(values));
    } catch (err) {
      setError(err.message || "Failed to load module details");
      setModuleDetails(null);
      setOriginalValues(null);
      setEditValues(null);
    } finally {
      setLoadingModuleDetails(false);
    }
  }, [auth, selectedModule, moduleType, searchField, useDev, apiUrl]);

  const handleModuleTypeChange = (nextModuleType) => {
    if (nextModuleType === moduleType) return;
    setModuleType(nextModuleType);
    setModuleSearchText("");
    setModuleOptions([]);
    resetDetails();
  };

  const handleUseDevChange = (checked) => {
    if (checked === useDev) return;
    setUseDev(checked);
    setModuleOptions([]);
    resetDetails();
  };

  const handleSearchFieldChange = (nextSearchField) => {
    if (nextSearchField === searchField) return;
    setSearchField(nextSearchField);
    setModuleOptions([]);
    resetDetails();
  };

  const handleModuleSearchChange = (value) => {
    setModuleSearchText(value);
    setUpdateStatus("");
    setUpdateResult(null);
  };

  const handleSelectedModuleChange = (value) => {
    setSelectedModule(value);
    setUpdateStatus("");
    setUpdateResult(null);
  };

  const updateField = (section, key, value) => {
    setEditValues((current) => ({
      ...cloneValues(current),
      [section]: {
        ...(current?.[section] || {}),
        [key]: value,
      },
    }));
    setUpdateStatus("");
    setUpdateResult(null);
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedModule || !moduleDetails || !hasPendingChanges) return;

    const shortName = MODULE_TYPE_TO_SHORT_NAME[moduleType];
    setUpdatingModule(true);
    setError("");
    setFieldErrors({});
    setUpdateStatus("");
    setUpdateResult(null);

    try {
      const res = await fetch(
        `${apiUrl}/api/v1/modules/${shortName}/${encodeURIComponent(selectedModule)}?dev=${
          useDev ? "true" : "false"
        }&identifier=${searchField}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${auth.user?.access_token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(changedPayload),
        }
      );

      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) {
        setUpdateResult(data?.detail || data);
        setFieldErrors(data?.detail?.fields || {});
        throw new Error(formatErrorPayload(data, `Failed to update module: ${res.status}`));
      }

      setUpdateStatus(data?.message || "Module update uploaded");
      setUpdateResult(data);
      const mergedValues = mergeChangedValues(editValues, data?.changes || changedPayload);
      setEditValues(cloneValues(mergedValues));
      setOriginalValues(cloneValues(mergedValues));
      setModuleDetails((current) =>
        current
          ? {
              ...current,
              values: cloneValues(mergedValues),
            }
          : current
      );
    } catch (err) {
      setError(err.message || "Failed to update module");
    } finally {
      setUpdatingModule(false);
    }
  };

  useEffect(() => {
    const currentSearch = normalizedSearch(location.search);
    if (lastWrittenSearch.current === currentSearch) return;

    const nextState = parseEditModuleQuery(location.search);
    applyingUrlState.current = true;
    setModuleType(nextState.moduleType);
    setSearchField(nextState.searchField);
    setModuleSearchText(nextState.moduleSearchText);
    setSelectedModule(nextState.selectedModule);
    setUseDev(nextState.useDev);
  }, [location.search]);

  useEffect(() => {
    if (applyingUrlState.current) {
      applyingUrlState.current = false;
      return;
    }

    const nextSearch = buildEditModuleSearch(location.search, {
      moduleType,
      searchField,
      moduleSearchText: debouncedModuleSearchText,
      selectedModule,
      useDev,
    });
    const currentSearch = normalizedSearch(location.search);

    if (nextSearch !== currentSearch) {
      lastWrittenSearch.current = nextSearch;
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
          hash: location.hash,
        },
        { replace: true }
      );
    }
  }, [
    moduleType,
    searchField,
    debouncedModuleSearchText,
    selectedModule,
    useDev,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  useEffect(() => {
    if (!moduleType) {
      setLoadingModules(false);
      setModuleOptions([]);
      resetDetails();
      return undefined;
    }

    if (moduleSearchText !== debouncedModuleSearchText) {
      setLoadingModules(true);
      setModuleOptions([]);
      return undefined;
    }

    const controller = new AbortController();
    fetchModuleOptions(debouncedModuleSearchText, controller.signal);

    return () => controller.abort();
  }, [
    moduleType,
    moduleSearchText,
    debouncedModuleSearchText,
    searchField,
    useDev,
    fetchModuleOptions,
    resetDetails,
  ]);

  useEffect(() => {
    if (selectedModule) {
      fetchModuleDetails();
    } else {
      clearLoadedDetails();
    }
  }, [selectedModule, useDev, fetchModuleDetails, clearLoadedDetails]);

  if (!auth.isAuthenticated) {
    return (
      <div style={{ padding: 20, maxWidth: 900, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h2>Edit Module</h2>
          <a href="/help#edit-module">Help</a>
        </div>
        <p>You must be logged in to view module details.</p>
        <button
          onClick={() => {
            const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            sessionStorage.setItem("post_login_redirect", returnTo);
            auth.signinRedirect({ state: { returnTo } });
          }}
        >
          Log in
        </button>
      </div>
    );
  }

  const renderTextField = (section, field) => {
    const commonProps = {
      value: fieldValue(editValues, section, field.key),
      onChange: (e) => updateField(section, field.key, e.target.value),
      style: { width: "100%", padding: 8, boxSizing: "border-box" },
    };

    return (
      <label key={`${section}-${field.key}`} style={{ display: "block", marginBottom: 12 }}>
        <span style={{ display: "block", fontSize: "0.9em", marginBottom: 4 }}>{field.label}</span>
        {field.type === "textarea" ? (
          <textarea {...commonProps} rows={3} maxLength={field.maxLength} />
        ) : (
          <input type={field.type === "date" ? "date" : "text"} {...commonProps} />
        )}
        {fieldErrors[field.key]?.length > 0 && (
          <span style={{ display: "block", color: "red", marginTop: 4 }}>
            {fieldErrors[field.key].join(" ")}
          </span>
        )}
      </label>
    );
  };

  const renderSelectField = (section, field) => (
    <label key={`${section}-${field.key}`} style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", fontSize: "0.9em", marginBottom: 4 }}>{field.label}</span>
      <select
        value={fieldValue(editValues, section, field.key)}
        onChange={(e) => updateField(section, field.key, e.target.value)}
        style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
      >
        <option value="">
          -- {field.emptyLabel || (field.required ? "select a value" : "no value")} --
        </option>
        {(field.options || []).map((option) => {
          const value = typeof option === "string" ? option : option.value;
          const label = typeof option === "string" ? option : option.label;
          return (
            <option key={value} value={value} disabled={Boolean(option?.disabled)}>
              {label}
            </option>
          );
        })}
      </select>
      {fieldErrors[field.key]?.length > 0 && (
        <span style={{ display: "block", color: "red", marginTop: 4 }}>
          {fieldErrors[field.key].join(" ")}
        </span>
      )}
    </label>
  );

  const renderEditableField = (section, field) => {
    if (field.type === "select") return renderSelectField(section, field);
    return renderTextField(section, field);
  };

  const schema = moduleDetails?.schema;

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: "0 auto", textAlign: "left" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2>Edit Module</h2>
        <a href="/help#edit-module">Help</a>
      </div>

      {error && <p style={{ color: "red", marginBottom: 16 }}>{error}</p>}
      {updateStatus && <p style={{ color: "green", marginBottom: 16 }}>{updateStatus}</p>}

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={useDev}
            onChange={(e) => handleUseDevChange(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          Use development database (dev)
        </label>
      </div>

      <div style={{ marginBottom: 20 }}>
        <h3>1. Select Module Type</h3>
        {MODULE_TYPE_OPTIONS.map((mt) => (
          <label key={mt} style={{ display: "block", marginBottom: 8 }}>
            <input
              type="radio"
              name="moduleType"
              value={mt}
              checked={moduleType === mt}
              onChange={(e) => handleModuleTypeChange(e.target.value)}
              style={{ marginRight: 8 }}
            />
            {mt}
          </label>
        ))}
      </div>

      {moduleType && (
        <div style={{ marginBottom: 20 }}>
          <h3>2. Select Module</h3>
          <div style={{ marginBottom: 8 }}>
            {Object.entries(SEARCH_FIELDS).map(([field, config]) => (
              <label key={field} style={{ marginRight: 16 }}>
                <input
                  type="radio"
                  name="moduleSearchField"
                  value={field}
                  checked={searchField === field}
                  onChange={(e) => handleSearchFieldChange(e.target.value)}
                  style={{ marginRight: 8 }}
                />
                {config.label}
              </label>
            ))}
          </div>
          <input
            type="text"
            placeholder={
              SEARCH_FIELDS[searchField]?.placeholder || SEARCH_FIELDS.NAME_LABEL.placeholder
            }
            value={moduleSearchText}
            onChange={(e) => handleModuleSearchChange(e.target.value)}
            style={{ width: "100%", padding: 8, marginBottom: 8, boxSizing: "border-box" }}
          />
          {loadingModules && <p>Loading modules...</p>}
          {!loadingModules && displayedModuleOptions.length > 0 && (
            <select
              aria-label="Module"
              value={selectedModule}
              onChange={(e) => handleSelectedModuleChange(e.target.value)}
              style={{ width: "100%", padding: 8 }}
              size={Math.min(displayedModuleOptions.length + 1, 10)}
            >
              <option value="">-- Select a module --</option>
              {displayedModuleOptions.map((mod) => (
                <option key={mod} value={mod}>
                  {mod}
                </option>
              ))}
            </select>
          )}
          {!loadingModules && displayedModuleOptions.length === 0 && (
            <p style={{ color: "#666" }}>No modules found for this type or search criteria.</p>
          )}
        </div>
      )}

      {selectedModule && (
        <div style={{ marginBottom: 20 }}>
          <h3>3. Module Details</h3>
          {loadingModuleDetails ? (
            <p>Loading module details...</p>
          ) : moduleDetails && editValues ? (
            <>
              <fieldset style={{ marginBottom: 20, padding: 12 }}>
                <legend>Identifiers</legend>
                {(schema?.readOnlyFields || []).map((field) => (
                  <label key={field.key} style={{ display: "block", marginBottom: 12 }}>
                    <span style={{ display: "block", fontSize: "0.9em", marginBottom: 4 }}>
                      {field.label}
                    </span>
                    <input
                      type="text"
                      value={fieldValue(editValues, "readOnly", field.key)}
                      disabled
                      style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                    />
                  </label>
                ))}
              </fieldset>

              <fieldset style={{ marginBottom: 20, padding: 12 }}>
                <legend>Part Fields</legend>
                {(schema?.partFields || []).map((field) => renderEditableField("part", field))}
              </fieldset>

              <fieldset style={{ marginBottom: 20, padding: 12 }}>
                <legend>Attributes</legend>
                {(schema?.attributeFields || []).map((field) =>
                  renderEditableField("attributes", field)
                )}
              </fieldset>

              <fieldset style={{ marginBottom: 20, padding: 12 }}>
                <legend>Module Extension</legend>
                {(schema?.extensionFields || []).map((field) =>
                  renderEditableField("extension", field)
                )}
              </fieldset>

              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button onClick={handleSave} disabled={!hasPendingChanges || updatingModule}>
                  {updatingModule ? "Saving..." : "Save changes"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditValues(cloneValues(originalValues))}
                  disabled={!hasPendingChanges || updatingModule}
                >
                  Reset changes
                </button>
                <button
                  type="button"
                  onClick={fetchModuleDetails}
                  disabled={loadingModuleDetails || updatingModule}
                >
                  Refresh from DB
                </button>
                {!hasPendingChanges && <span style={{ color: "#666" }}>No pending changes</span>}
              </div>

              {updateResult && (
                <details style={{ marginTop: 16 }}>
                  <summary>Upload details</summary>
                  <pre
                    style={{
                      background: "#f6f8fa",
                      padding: 12,
                      borderRadius: 4,
                      overflowX: "auto",
                    }}
                  >
                    {JSON.stringify(updateResult, null, 2)}
                  </pre>
                </details>
              )}
            </>
          ) : (
            <p>No details available for the selected module.</p>
          )}
        </div>
      )}
    </div>
  );
}
